import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Fade,
  alpha,
  KeyboardArrowUpIcon,
  KeyboardArrowDownIcon,
} from '@/ui';

// State machine states
type PillState = 'hidden' | 'appearing' | 'visible' | 'disappearing';

interface ScrollPillProps {
  /** Ref to the scrollable container element */
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  /** Whether the bottom navigation bar is visible (affects positioning) */
  navVisible?: boolean;
  /** Bottom offset in px when navVisible=true (default: 96) */
  navBottomOffsetPx?: number;
  /** Bottom offset in px when navVisible=false (default: 24) */
  noNavBottomOffsetPx?: number;
  /** Optional threshold for velocity detection (default: 20px) */
  velocityThreshold?: number;
  /** Optional auto-hide timeout in ms (default: 800ms) */
  autoHideDelay?: number;
  /** Optional boundary hint behavior: show "Scroll down" when at top (default: false) */
  showAtTop?: boolean;
  /** Optional boundary hint behavior: show "Scroll up" when at bottom (default: false) */
  showAtBottom?: boolean;
  /** How long to keep boundary hints visible before hiding (default: 750ms) */
  boundaryHoldDelay?: number;
  /** Minimum scrollable height before enabling pill (default: 50px) */
  minScrollableHeight?: number;
  /** Hide pill at boundaries (default: true) */
  hideAtBoundaries?: boolean;
  /** Scroll behavior on tap (default: "smooth") */
  scrollBehavior?: 'smooth' | 'instant';
}

interface ScrollState {
  lastScrollY: number;
  direction: 'up' | 'down' | null;
  inactivityTimer: NodeJS.Timeout | null;
  atTop: boolean;
  atBottom: boolean;
  isAutoScrolling: boolean;
}

const ScrollPill: React.FC<ScrollPillProps> = ({
  scrollContainerRef,
  navVisible = true,
  navBottomOffsetPx = 96,
  noNavBottomOffsetPx = 24,
  velocityThreshold = 20,
  autoHideDelay = 800,
  showAtTop = false,
  showAtBottom = false,
  boundaryHoldDelay = 750,
  minScrollableHeight = 50,
  hideAtBoundaries = true,
  scrollBehavior = 'smooth',
}) => {
  const [pillState, setPillState] = useState<PillState>('hidden');
  const [showDirection, setShowDirection] = useState<'up' | 'down' | null>(null);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  const bottomOffset = navVisible ? navBottomOffsetPx : noNavBottomOffsetPx;
  const bottomCss = `calc(${bottomOffset}px + env(safe-area-inset-bottom))`;
  
  const scrollStateRef = useRef<ScrollState>({
    lastScrollY: 0,
    direction: null,
    inactivityTimer: null,
    atTop: true,
    atBottom: false,
    isAutoScrolling: false,
  });
  
  const rafRef = useRef<number | null>(null);
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const isTextInputElement = useCallback((el: Element | null): boolean => {
    if (!el) return false;
    if (el instanceof HTMLTextAreaElement) return true;
    if (el instanceof HTMLSelectElement) return true;
    if (el instanceof HTMLInputElement) {
      const t = (el.type || '').toLowerCase();
      // Treat most input types as keyboard-opening. Exclude non-text inputs.
      return !['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color'].includes(t);
    }
    const maybe = el as HTMLElement;
    return Boolean(maybe.isContentEditable);
  }, []);

  // Detect mobile keyboard open using visualViewport + focused input heuristic.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const vv = window.visualViewport;
    const KEYBOARD_SHRINK_PX = 100;

    const update = () => {
      const active = document.activeElement;
      const focusedInput = isTextInputElement(active);
      const layoutHeight = window.innerHeight;
      const visualHeight = vv?.height ?? layoutHeight;
      const shrink = layoutHeight - visualHeight;
      const nextOpen = focusedInput && shrink > KEYBOARD_SHRINK_PX;
      setIsKeyboardOpen(nextOpen);
    };

    update();

    document.addEventListener('focusin', update, true);
    document.addEventListener('focusout', update, true);
    window.addEventListener('resize', update);
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);

    return () => {
      document.removeEventListener('focusin', update, true);
      document.removeEventListener('focusout', update, true);
      window.removeEventListener('resize', update);
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
    };
  }, [isTextInputElement]);

  // Check if container has enough scrollable content
  const hasEnoughScrollableContent = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return false;
    const scrollableHeight = container.scrollHeight - container.clientHeight;
    return scrollableHeight >= minScrollableHeight;
  }, [scrollContainerRef, minScrollableHeight]);

  // Check boundary conditions
  const checkBoundaries = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return { atTop: true, atBottom: false };
    
    const scrollY = container.scrollTop;
    // Handle iOS negative scroll bounce
    const atTop = scrollY <= 0;
    const atBottom = scrollY >= container.scrollHeight - container.clientHeight - 2; // 2px tolerance
    
    return { atTop, atBottom };
  }, [scrollContainerRef]);

  // Start hide timer
  const startHideTimer = useCallback((delayMs?: number) => {
    const state = scrollStateRef.current;
    
    if (state.inactivityTimer) {
      clearTimeout(state.inactivityTimer);
    }
    
    state.inactivityTimer = setTimeout(() => {
      setPillState('disappearing');
      // After animation completes, set to hidden
      setTimeout(() => {
        setPillState('hidden');
        setShowDirection(null);
      }, 200); // Match fade duration
    }, delayMs ?? autoHideDelay);
  }, [autoHideDelay]);

  // Clear hide timer
  const clearHideTimer = useCallback(() => {
    const state = scrollStateRef.current;
    if (state.inactivityTimer) {
      clearTimeout(state.inactivityTimer);
      state.inactivityTimer = null;
    }
  }, []);

  // Hide pill immediately (used for keyboard-open and other hard hides)
  const hideImmediately = useCallback(() => {
    clearHideTimer();
    setPillState('hidden');
    setShowDirection(null);
  }, [clearHideTimer]);

  // Hide pill while keyboard is open
  useEffect(() => {
    if (!isKeyboardOpen) return;
    hideImmediately();
  }, [isKeyboardOpen, hideImmediately]);

  // Handle scroll with velocity detection
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const state = scrollStateRef.current;
    
    // Skip if auto-scrolling (triggered by pill click)
    if (state.isAutoScrolling) return;

    // Hide / disable while keyboard is open (avoid overlap with inputs)
    if (isKeyboardOpen) {
      hideImmediately();
      return;
    }
    
    // Check if enough scrollable content
    if (!hasEnoughScrollableContent()) {
      setPillState('hidden');
      return;
    }

    const currentScrollY = container.scrollTop;
    const deltaY = currentScrollY - state.lastScrollY;
    const absDeltaY = Math.abs(deltaY);
    
    // Check boundaries
    const prevAtTop = state.atTop;
    const prevAtBottom = state.atBottom;
    const { atTop, atBottom } = checkBoundaries();
    state.atTop = atTop;
    state.atBottom = atBottom;
    
    // Determine scroll direction (we show the SAME direction on the pill)
    if (deltaY > 0) {
      state.direction = 'down';
    } else if (deltaY < 0) {
      state.direction = 'up';
    }
    
    // Update last scroll position
    state.lastScrollY = currentScrollY;

    // If the pill is already visible, keep it alive while the user keeps scrolling
    // (spec prefers timer reset on every scroll event).
    if (showDirection && pillState === 'visible') {
      clearHideTimer();
      startHideTimer();
    }

    // Boundary hint behavior (optional)
    // When the user reaches TOP: optionally show "Scroll down" briefly, otherwise hide.
    if (atTop && !prevAtTop) {
      if (showAtTop) {
        setShowDirection('down');
        setPillState('visible');
        clearHideTimer();
        startHideTimer(boundaryHoldDelay);
      } else if (hideAtBoundaries) {
        hideImmediately();
      }
      // Continue: reaching top still allows fast-scroll logic to run, but this ensures the
      // boundary behavior takes effect on first contact.
    }

    // When the user reaches BOTTOM: optionally show "Scroll up" briefly, otherwise hide.
    if (atBottom && !prevAtBottom) {
      if (showAtBottom) {
        setShowDirection('up');
        setPillState('visible');
        clearHideTimer();
        startHideTimer(boundaryHoldDelay);
      } else if (hideAtBoundaries) {
        hideImmediately();
      }
    }
    
    // Only show pill for fast/intentional scrolling
    if (absDeltaY >= velocityThreshold && state.direction) {
      // Show the same direction (so "Scroll up" scrolls up, etc.)
      const pillDirection = state.direction;
      
      // Check boundary conditions
      if (hideAtBoundaries) {
        // At top and would show "Scroll up" - hide it
        if (atTop && pillDirection === 'up') {
          if (showAtTop) {
            setShowDirection('down');
            setPillState('visible');
            clearHideTimer();
            startHideTimer(boundaryHoldDelay);
            return;
          }
          hideImmediately();
          return;
        }
        // At bottom and would show "Scroll down" - hide it  
        if (atBottom && pillDirection === 'down') {
          if (showAtBottom) {
            setShowDirection('up');
            setPillState('visible');
            clearHideTimer();
            startHideTimer(boundaryHoldDelay);
            return;
          }
          hideImmediately();
          return;
        }
      }
      
      setShowDirection(pillDirection);
      setPillState('visible');
      
      // Reset and start inactivity timer
      clearHideTimer();
      startHideTimer();
    }
  }, [
    scrollContainerRef,
    velocityThreshold,
    isKeyboardOpen,
    hideImmediately,
    hideAtBoundaries,
    showAtTop,
    showAtBottom,
    boundaryHoldDelay,
    hasEnoughScrollableContent,
    checkBoundaries,
    pillState,
    showDirection,
    clearHideTimer,
    startHideTimer,
  ]);

  // Throttled scroll handler using requestAnimationFrame
  const throttledScrollHandler = useCallback(() => {
    if (rafRef.current !== null) return;
    
    rafRef.current = requestAnimationFrame(() => {
      handleScroll();
      rafRef.current = null;
    });
  }, [handleScroll]);

  // Handle pill click - scroll to top or bottom
  const handlePillClick = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !showDirection) return;
    
    const state = scrollStateRef.current;
    
    // Mark as auto-scrolling to prevent re-showing pill
    state.isAutoScrolling = true;
    
    // Clear any pending hide timer
    clearHideTimer();
    
    // Start hiding pill immediately
    setPillState('disappearing');
    
    // Scroll to target (clamp to max scrollTop)
    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
    const targetY = showDirection === 'up' ? 0 : maxScrollTop;
    
    const normalizedBehavior =
      prefersReducedMotion || scrollBehavior === 'instant' ? 'auto' : 'smooth';

    container.scrollTo({
      top: targetY,
      behavior: normalizedBehavior,
    });
    
    // Reset auto-scrolling flag only once the programmatic scroll is actually finished.
    const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const maxWaitMs = prefersReducedMotion || scrollBehavior === 'instant' ? 300 : 2000;
    let lastY = container.scrollTop;
    let stableFrames = 0;

    const finish = () => {
      state.isAutoScrolling = false;
      state.lastScrollY = container.scrollTop;
      setPillState('hidden');
      setShowDirection(null);
    };

    const tick = () => {
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const y = container.scrollTop;
      const atTarget = Math.abs(y - targetY) <= 1;
      const moved = Math.abs(y - lastY);
      lastY = y;

      // Consider scroll finished when we've reached the target or when scroll has stabilized.
      if (atTarget || moved < 0.5) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
      }

      if (stableFrames >= 5 || now - startTime > maxWaitMs) {
        finish();
        return;
      }

      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, [scrollContainerRef, showDirection, prefersReducedMotion, scrollBehavior, clearHideTimer]);

  // Handle keyboard interaction
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePillClick();
    }
  }, [handlePillClick]);

  // Setup scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    container.addEventListener('scroll', throttledScrollHandler, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', throttledScrollHandler);
      clearHideTimer();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [scrollContainerRef, throttledScrollHandler, clearHideTimer]);

  // Dynamic aria-label based on direction
  const ariaLabel = showDirection === 'up' ? 'Scroll to top' : 'Scroll to bottom';
  
  // Determine visibility for Fade
  const isVisible = pillState === 'visible' || pillState === 'appearing';
  
  // Don't render if hidden
  if (pillState === 'hidden' && !showDirection) {
    return null;
  }

  return (
    <Fade in={isVisible} timeout={prefersReducedMotion ? 0 : 200}>
      <Box data-testid="components-scroll-pill-container-handle-pill-click"
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-live="polite"
        onClick={handlePillClick}
        onKeyDown={handleKeyDown}
        sx={{
          position: 'fixed',
          left: '50%',
          transform: 'translateX(-50%)',
          // Position above BottomTabBar when nav visible (96px), otherwise closer to bottom (24px)
          bottom: bottomCss,
          // Ensure pill is above BottomTabBar (zIndex: 1200)
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 2,
          py: 1,
          borderRadius: '20px',
          // Reverse color scheme: white background with green content (requested)
          backgroundColor: alpha('#ffffff', 0.95),
          backdropFilter: 'blur(8px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)',
          cursor: 'pointer',
          userSelect: 'none',
          transition: prefersReducedMotion 
            ? 'none' 
            : 'transform 0.15s ease-out, box-shadow 0.15s ease-out, bottom 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateX(-50%) scale(1.02)',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2), 0 3px 6px rgba(0, 0, 0, 0.12)',
          },
          '&:active': {
            transform: 'translateX(-50%) scale(0.98)',
          },
          '&:focus-visible': {
            // Keep focus ring visible on white background
            outline: '2px solid #155f54',
            outlineOffset: 2,
          },
          // Ensure visibility on iOS
          WebkitBackfaceVisibility: 'hidden',
        }}
      >
        {showDirection === 'up' ? (
          <KeyboardArrowUpIcon sx={{ color: '#155f54', fontSize: 20 }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ color: '#155f54', fontSize: 20 }} />
        )}
        <Typography data-testid="components-scroll-pill-text-action"
          sx={{
            color: '#155f54',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {showDirection === 'up' ? 'Scroll up' : 'Scroll down'}
        </Typography>
      </Box>
    </Fade>
  );
};

export default ScrollPill;
